import { Box, Flex, HStack, Text, type BoxProps } from "@chakra-ui/react";
import { Calendar, Copy, Package, Share2, UserPlus } from "lucide-react";
import FadingImage from "../common/FadingImage";
import { HeroCTAButton, HeroSecondaryButton } from "../Button";
import {
  AnimatePresence,
  motion,
  type Variants,
} from "framer-motion";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { ONETIME_FROM_RATE_LABEL } from "../../constants/pricing";
import { writeToClipboard } from "../../utils/clipboard";

const MotionBox = motion.create(Box);
const MotionText = motion.create(Text);
const MotionFlex = motion.create(Flex);

interface SlideItem {
  kind?: "referral";
  image: string;
  eyebrow: string;
  title: string;
  description: string;
  guestDescription?: string;
  primaryCta: string;
  primaryPath?: string;
  primaryIcon?: "calendar" | "package";
  secondaryCta: string;
  secondaryPath: string;
  textAlign: "left" | "right";
  objectPosition?: {
    base?: string;
    sm?: string;
    tablet?: string;
    desktop?: string;
  };
}

const slides: SlideItem[] = [
  {
    image: "/home/carou1.webp",
    eyebrow: "Trusted Home Cleaning in Singapore",
    title: "Come Home to Clean — Every Single Time",
    description: `Trusted cleaners, flexible schedules, and a home that actually feels clean. From ${ONETIME_FROM_RATE_LABEL}.`,
    primaryCta: "Book Now",
    primaryPath: "/book/cleaning/general-cleaning",
    secondaryCta: "View Pricing",
    secondaryPath: "/services/classic-cleaning#service-pricing",
    textAlign: "left",
    objectPosition: {
      base: "center center",
      sm: "center center",
      tablet: "center center",
      desktop: "24% 40%",
    },
  },
  {
    image: "/home/carou2.webp",
    eyebrow: "Prepay & Save on Every Session",
    title: "Book a Bundle. Pay Less. Clean More.",
    description: "Get a bundle of cleaning sessions at a discounted rate — credits are yours to use at your own pace.",
    primaryCta: "View Packages",
    primaryPath: "/packages",
    primaryIcon: "package",
    secondaryCta: "View Pricing",
    secondaryPath: "/services/classic-cleaning#session-packages",
    textAlign: "right",
    objectPosition: {
      base: "80% center",
      sm: "center center",
      tablet: "20% 10%",
      desktop: "100% 40%",
    },
  },
  {
    image: "/home/carou3.webp",
    kind: "referral",
    eyebrow: "Refer & Earn",
    title: "Invite friends. Earn free cleaning hours.",
    guestDescription: "Create an account to get your personal invite link and start earning free cleaning hours.",
    description: "Share your HomeHelpy link. When your friend completes their first clean, you get 1 free hour credited automatically.",
    primaryCta: "Share link",
    secondaryCta: "Copy code",
    secondaryPath: "/book/cleaning/general-cleaning",
    textAlign: "left",
    objectPosition: {
      base: "75% center",
      sm: "70% center",
      tablet: "center 90%",
      desktop: "28% 70%",
    },
  },
  {
    image: "/home/carou4.webp",
    eyebrow: "Aircon Servicing in Singapore",
    title: "Cool, Clean Air — The Way It Should Be",
    description: "Chemical wash, filter deep-clean, and performance checks from trained technicians. One-time or quarterly plans, from $18/unit.",
    primaryCta: "Book Aircon Servicing",
    primaryPath: "/book/aircon/general-servicing",
    secondaryCta: "View Pricing",
    secondaryPath: "/services/aircon-servicing#service-pricing",
    textAlign: "right",
    objectPosition: {
      base: "15% 80%",
      sm: "70% center",
      tablet: "center 15%",
      desktop: "70% 15%",
    },
  },
  {
    image: "/home/carou5.webp",
    eyebrow: "Clean Office, Focused Team",
    title: "Your Office Should Look as Professional as Your Work",
    description: "Quiet, reliable office cleaning that runs in the background — so your workspace is always ready for business.",
    primaryCta: "Book Office Cleaning",
    secondaryCta: "Office Cleaning",
    secondaryPath: "/services/office-cleaning",
    textAlign: "left",
    objectPosition: {
      base: "63% center",
      sm: "center center",
      tablet: "center center",
      desktop: "26% 28%",
    },
  },
];

const imageVariants: Variants = {
  enter: { opacity: 0 },
  center: { opacity: 1, transition: { duration: 0.85, ease: "easeInOut" } },
  exit: { opacity: 0, transition: { duration: 0.85, ease: "easeInOut" } },
};

const eyebrowVariants: Variants = {
  hidden: { opacity: 0, y: 14, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.45, ease: "easeOut", delay: 0.08 },
  },
};

const titleVariants: Variants = {
  hidden: { opacity: 0, y: 28, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.62, ease: "easeOut", delay: 0.18 },
  },
};

const descriptionVariants: Variants = {
  hidden: { opacity: 0, y: 18, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.52, ease: "easeOut", delay: 0.34 },
  },
};

const ctaVariants: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.48, ease: "easeOut", delay: 0.5 },
  },
};

export default function HeroCarousel() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [copiedReferral, setCopiedReferral] = useState(false);
  const [copyFailedReferral, setCopyFailedReferral] = useState(false);
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const timerRef = useRef<number | null>(null);
  const copiedTimerRef = useRef<number | null>(null);

  const startTimer = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 9000);
  };

  const pauseTimer = () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    startTimer();
    const resume = () => startTimer();
    window.addEventListener("hh:drawer-open", pauseTimer);
    window.addEventListener("hh:drawer-close", resume);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      window.removeEventListener("hh:drawer-open", pauseTimer);
      window.removeEventListener("hh:drawer-close", resume);
    };
  }, []);

  useEffect(() => {
    const userId = user?.firebase_id;
    if (!userId) {
      setReferralCode(null);
      return;
    }

    let cancelled = false;
    import("../../utils/accountCache")
      .then(({ getCachedRewards, REWARDS_TTL_MS }) =>
        getCachedRewards(userId, REWARDS_TTL_MS),
      )
      .then((result) => {
        if (cancelled) return;
        setReferralCode(result.data?.referral?.referralCode ?? null);
      })
      .catch(() => {
        if (!cancelled) setReferralCode(null);
      });

    return () => {
      cancelled = true;
    };
  }, [user?.firebase_id]);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  const showCopiedFeedback = useCallback(() => {
    setCopyFailedReferral(false);
    setCopiedReferral(true);
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = window.setTimeout(() => {
      setCopiedReferral(false);
      copiedTimerRef.current = null;
    }, 2000);
  }, []);

  const showCopyFailedFeedback = useCallback(() => {
    setCopiedReferral(false);
    setCopyFailedReferral(true);
    if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = window.setTimeout(() => {
      setCopyFailedReferral(false);
      copiedTimerRef.current = null;
    }, 2000);
  }, []);

  const copyToClipboard = useCallback(
    async (value: string) => {
      const ok = await writeToClipboard(value);
      if (ok) showCopiedFeedback();
      else showCopyFailedFeedback();
    },
    [showCopiedFeedback, showCopyFailedFeedback],
  );

  const getReferralLink = useCallback(() => {
    if (!referralCode) return "";
    return `${window.location.origin}/ref/${referralCode}`;
  }, [referralCode]);

  const shareReferralLink = useCallback(async () => {
    const referralLink = getReferralLink();
    if (!referralLink) {
      navigate("/dashboard");
      return;
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join me on HomeHelpy",
          text: "Sign up with my HomeHelpy link and earn rewards with me.",
          url: referralLink,
        });
        return;
      } catch {
        // Fall back to copying if sharing is dismissed or unavailable.
      }
    }

    await copyToClipboard(referralLink);
  }, [copyToClipboard, getReferralLink, navigate]);

  const goTo = (index: number) => {
    setActiveIndex(index);
    startTimer();
  };

  const activeSlide = slides[activeIndex];
  const isRight = activeSlide.textAlign === "right";
  const isReferralSlide = activeSlide.kind === "referral";
  const isLoggedIn = Boolean(user);
  const description =
    isReferralSlide && !isLoggedIn && activeSlide.guestDescription
      ? activeSlide.guestDescription
      : activeSlide.description;
  const primaryCta = isReferralSlide
    ? isLoggedIn
      ? "Share link"
      : "Register now"
    : activeSlide.primaryCta;
  const secondaryCta = isReferralSlide
    ? isLoggedIn
      ? copiedReferral
        ? "Copied"
        : copyFailedReferral
          ? "Copy failed"
          : "Copy code"
      : "Book now"
    : activeSlide.secondaryCta;
  const primaryIcon = isReferralSlide
    ? isLoggedIn
      ? <Share2 size={16} />
      : <UserPlus size={16} />
    : activeSlide.primaryIcon === "package"
      ? <Package size={16} />
      : <Calendar size={16} />;
  const secondaryIcon = isReferralSlide
    ? isLoggedIn
      ? <Copy size={16} />
      : <Calendar size={16} />
    : undefined;

  const handlePrimaryCta = () => {
    if (isReferralSlide) {
      if (isLoggedIn) {
        void shareReferralLink();
        return;
      }
      navigate("/signup");
      return;
    }

    navigate(activeSlide.primaryPath ?? "/dashboard");
  };

  const handleSecondaryCta = () => {
    if (isReferralSlide) {
      if (isLoggedIn) {
        if (referralCode) {
          void copyToClipboard(referralCode);
        } else {
          navigate("/dashboard");
        }
        return;
      }
      navigate(activeSlide.secondaryPath);
      return;
    }

    navigate(activeSlide.secondaryPath);
  };

  return (
    <Box
      id="home-hero"
      position="relative"
      w="100vw"
      left="50%"
      ml="-50vw"
      overflow="hidden"
      onMouseEnter={pauseTimer}
      onMouseLeave={startTimer}
    >
      <Flex
        position="relative"
        w="100%"
        h={{ base: "34rem", sm: "36rem", tablet: "38rem", desktop: "42rem" }}
      >
        {/* Crossfade image layer */}
        <AnimatePresence initial={false}>
          <MotionBox
            key={activeIndex}
            variants={imageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            position="absolute"
            inset={0}
            style={{ willChange: "opacity" }}
          >
            <FadingImage
              src={activeSlide.image}
              alt={activeSlide.title}
              w="100%"
              h="100%"
              objectFit="cover"
              objectPosition={{
                base: activeSlide.objectPosition?.base || "center center",
                sm:
                  activeSlide.objectPosition?.sm ||
                  activeSlide.objectPosition?.base ||
                  "center center",
                tablet:
                  activeSlide.objectPosition?.tablet ||
                  activeSlide.objectPosition?.sm ||
                  activeSlide.objectPosition?.base ||
                  "center center",
                desktop:
                  activeSlide.objectPosition?.desktop ||
                  activeSlide.objectPosition?.tablet ||
                  activeSlide.objectPosition?.sm ||
                  activeSlide.objectPosition?.base ||
                  "center center",
              }}
              pointerEvents="none"
              draggable={false}
              skeletonStartColor="gray.800"
              skeletonEndColor="gray.700"
            />
          </MotionBox>
        </AnimatePresence>

        {/* Mobile overlay */}
        <Box
          position="absolute"
          inset={0}
          zIndex={1}
          display={{ base: "block", tablet: "none" }}
          bg="linear-gradient(180deg, rgba(10,16,14,0.80) 0%, rgba(10,16,14,0.65) 36%, rgba(10,16,14,0.82) 100%)"
          pointerEvents="none"
        />

        {/* Desktop left-to-right overlay */}
        <Box
          position="absolute"
          inset={0}
          zIndex={1}
          display={{ base: "none", tablet: "block" }}
          bg="linear-gradient(90deg, rgba(10,16,14,0.92) 0%, rgba(10,16,14,0.74) 24%, rgba(10,16,14,0.42) 46%, rgba(10,16,14,0.18) 66%, rgba(10,16,14,0) 100%)"
          pointerEvents="none"
          style={{ opacity: isRight ? 0 : 1, transition: "opacity 0.9s ease-in-out", willChange: "opacity" }}
        />

        {/* Desktop right-to-left overlay */}
        <Box
          position="absolute"
          inset={0}
          zIndex={1}
          display={{ base: "none", tablet: "block" }}
          bg="linear-gradient(270deg, rgba(10,16,14,0.92) 0%, rgba(10,16,14,0.74) 24%, rgba(10,16,14,0.42) 46%, rgba(10,16,14,0.18) 66%, rgba(10,16,14,0) 100%)"
          pointerEvents="none"
          style={{ opacity: isRight ? 1 : 0, transition: "opacity 0.9s ease-in-out", willChange: "opacity" }}
        />

        {/* Top-edge gradient */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          h="10rem"
          zIndex={1}
          bg="linear-gradient(to bottom, rgba(0,0,0,0.48) 0%, rgba(0,0,0,0.18) 55%, rgba(0,0,0,0) 100%)"
          pointerEvents="none"
        />

        {/* Text content */}
        <Flex
          position="relative"
          zIndex={2}
          w="100%"
          maxW="1512px"
          mx="auto"
          px={{
            base: "1.6rem",
            sm: "2rem",
            tablet: "2.5rem",
            desktop: "6.25rem",
          }}
          pt={{ base: "6.5rem", tablet: "7.2rem", desktop: "8rem" }}
          pb={{ base: "4rem", tablet: "4.5rem", desktop: "5rem" }}
          align="center"
          pointerEvents="none"
        >
          <MotionBox
            key={`content-${activeIndex}`}
            initial="hidden"
            animate="visible"
            w="100%"
            maxW={{ base: "100%", tablet: "32rem", desktop: "38rem" }}
            ml={{
              base: "auto",
              tablet: activeSlide.textAlign === "right" ? "auto" : "0",
            }}
            mr={{
              base: "auto",
              tablet: activeSlide.textAlign === "right" ? "0" : "auto",
            }}
            textAlign={{ base: "center", tablet: activeSlide.textAlign }}
          >
            <MotionText
              variants={eyebrowVariants}
              mb="0.9rem"
              color="primary.surface.lighter"
              fontWeight="700"
              letterSpacing="0.2em"
              textTransform="uppercase"
              fontSize={{ base: "0.82rem", tablet: "0.95rem" }}
              textShadow={{
                base: "0 2px 5px rgba(0,0,0,0.38)",
                tablet: "0 2px 10px rgba(0,0,0,0.22)",
              }}
            >
              {activeSlide.eyebrow}
            </MotionText>

            <MotionText
              variants={titleVariants}
              color="white"
              fontWeight="800"
              lineHeight={{ base: "1.08", desktop: "1.02" }}
              fontSize={{ base: "2rem", tablet: "3rem", desktop: "4.1rem" }}
              maxW={{ base: "100%", tablet: "36rem" }}
              ml={{
                base: "auto",
                tablet: activeSlide.textAlign === "right" ? "auto" : "0",
              }}
              mr={{
                base: "auto",
                tablet: activeSlide.textAlign === "right" ? "0" : "auto",
              }}
              textShadow="0 4px 20px rgba(0,0,0,0.34)"
            >
              {activeSlide.title}
            </MotionText>

            <MotionText
              variants={descriptionVariants}
              mt="1rem"
              color="whiteAlpha.950"
              lineHeight="1.85"
              fontSize={{
                base: "0.96rem",
                tablet: "1.02rem",
                desktop: "1.08rem",
              }}
              maxW={{ base: "100%", tablet: "31rem" }}
              ml={{
                base: "auto",
                tablet: activeSlide.textAlign === "right" ? "auto" : "0",
              }}
              mr={{
                base: "auto",
                tablet: activeSlide.textAlign === "right" ? "0" : "auto",
              }}
              textShadow="0 2px 12px rgba(0,0,0,0.28)"
            >
              {description}
            </MotionText>

            <MotionFlex
              variants={ctaVariants}
              mt="1.8rem"
              gap={{ base: "0.7rem", tablet: "0.85rem" }}
              direction={{ base: "column", tablet: "row" }}
              align="center"
              w={{ base: "13rem", tablet: "auto" }}
              mx={{ base: "auto", tablet: "0" }}
              justify={{
                base: "center",
                tablet:
                  activeSlide.textAlign === "right" ? "flex-end" : "flex-start",
              }}
              pointerEvents="all"
            >
              <HeroCTAButton
                onClick={handlePrimaryCta}
                icon={primaryIcon}
                w={{ base: "full", tablet: "auto" }}
                px={{ base: "1.4rem", tablet: "1.5rem" }}
              >
                {primaryCta}
              </HeroCTAButton>

              <HeroSecondaryButton
                onClick={handleSecondaryCta}
                icon={secondaryIcon}
              >
                {secondaryCta}
              </HeroSecondaryButton>
            </MotionFlex>
          </MotionBox>
        </Flex>

        {/* Dot indicators */}
        <HStack
          position="absolute"
          left="50%"
          bottom={{ base: "1.2rem", tablet: "1.4rem", desktop: "1.6rem" }}
          transform="translateX(-50%)"
          zIndex={3}
          gap="0.55rem"
        >
          {slides.map((_, index) => (
            <Box
              key={index}
              as="button"
              {...({ type: "button" } as BoxProps)}
              aria-label={`Go to slide ${index + 1}`}
              w={index === activeIndex ? "2rem" : "0.65rem"}
              h="0.2rem"
              rounded="full"
              bg={
                index === activeIndex
                  ? "white"
                  : "rgba(255,255,255,0.4)"
              }
              transition="all 0.4s ease"
              cursor="pointer"
              onClick={() => goTo(index)}
            />
          ))}
        </HStack>
      </Flex>
    </Box>
  );
}
